import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'app/auth/auth.service';
import { ILinkApprovalResult, ILinkApprovalTokenState } from 'app/models/common/account-link-approval';
import { AccountLinkApprovalService } from 'app/services/common/account-link-approval.service';

@Component({
  selector: 'app-account-link-approval',
  templateUrl: './account-link-approval.component.html',
  styleUrls: ['./account-link-approval.component.scss']
})
export class AccountLinkApprovalComponent implements OnInit {
  public token = '';
  public requestType = '';
  public entityType = '';
  public entityName = '';
  public individualName = '';
  public requestedUserEmail = '';
  public finalDecision: 'Approved' | 'Denied' | '' = '';
  public finalDecisionByEmail = '';
  public finalDecisionAt = '';
  public state: 'loading' | 'ready' | 'approving' | 'denying' | 'approved' | 'denied' | 'alreadyApproved' | 'alreadyDenied' | 'invalid' | 'expired' | 'used' | 'revoked' | 'error' = 'loading';
  public detailMessage = '';

  get finalDecisionLabel(): string {
    return this.finalDecision || 'Processed';
  }

  get finalDecisionSummary(): string {
    if (!this.finalDecision) {
      return '';
    }

    if (this.finalDecisionByEmail) {
      return `This request was ${this.finalDecision.toLowerCase()} by ${this.finalDecisionByEmail}.`;
    }

    return `This request was already ${this.finalDecision.toLowerCase()}.`;
  }

  get canTakeAction(): boolean {
    return this.state === 'ready';
  }

  get showEntityContextRow(): boolean {
    if (!this.hasText(this.entityName)) {
      return false;
    }

    if (!this.hasText(this.individualName)) {
      return true;
    }

    const normalizedEntityLabel = this.normalizeForComparison(this.entityType || this.requestType);
    const normalizedEntityName = this.normalizeForComparison(this.entityName);
    const normalizedIndividualName = this.normalizeForComparison(this.individualName);

    const isIndividualRequest = normalizedEntityLabel === 'individual';
    const isDuplicateName = normalizedEntityName.length > 0 && normalizedEntityName === normalizedIndividualName;

    return !(isIndividualRequest && isDuplicateName);
  }

  get returnRoute(): string {
    return this.authService.isAuthenticated() ? '/dashboard' : '/home';
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly accountLinkApprovalService: AccountLinkApprovalService,
  ) {}

  ngOnInit(): void {
    this.token = (this.route.snapshot.queryParamMap.get('token') || '').trim();

    if (!this.token) {
      this.state = 'invalid';
      this.detailMessage = 'The approval link is missing a token.';
      return;
    }

    this.loadTokenState();
  }

  approveRequest(): void {
    if (!this.token || !this.canTakeAction) {
      return;
    }

    this.state = 'approving';

    this.accountLinkApprovalService.approveToken(this.token).subscribe({
      next: (result) => this.applyApprovalResult(result),
      error: () => {
        this.state = 'error';
        this.detailMessage = 'Unable to process this approval request right now. Please try again later.';
      }
    });
  }

  denyRequest(): void {
    if (!this.token || !this.canTakeAction) {
      return;
    }

    this.state = 'denying';

    this.accountLinkApprovalService.denyToken(this.token).subscribe({
      next: (result) => this.applyApprovalResult(result),
      error: () => {
        this.state = 'error';
        this.detailMessage = 'Unable to process this denial request right now. Please try again later.';
      }
    });
  }

  private loadTokenState(): void {
    this.state = 'loading';

    this.accountLinkApprovalService.validateToken(this.token).subscribe({
      next: (tokenState) => this.applyTokenState(tokenState),
      error: () => {
        this.state = 'error';
        this.detailMessage = 'Unable to validate this approval link right now. Please try again later.';
      }
    });
  }

  private applyTokenState(tokenState: ILinkApprovalTokenState): void {
    this.applyRequestDetails(tokenState?.requestType, tokenState?.entityType, tokenState?.entityName, tokenState?.individualName, tokenState?.requestedUserEmail);
    this.applyFinalDecisionDetails(tokenState?.finalDecision, tokenState?.finalDecisionByEmail, tokenState?.finalDecisionAt);

    if (tokenState?.isValid) {
      this.state = 'ready';
      this.detailMessage = this.entityType
        ? `Approve account access for ${this.entityType}.`
        : 'Approve this account-link request.';
      return;
    }

    if (tokenState?.isExpired) {
      this.state = 'expired';
      this.detailMessage = this.buildUnavailableMessage('This approval link has expired.');
      return;
    }

    if (tokenState?.isUsed) {
      this.state = 'used';
      this.detailMessage = this.buildUnavailableMessage('This approval link has already been used.');
      return;
    }

    if (tokenState?.isRevoked) {
      this.state = 'revoked';
      this.detailMessage = this.buildUnavailableMessage('This approval link has been revoked.');
      return;
    }

    this.state = 'invalid';
    this.detailMessage = 'This approval link is invalid.';
  }

  private applyApprovalResult(result: ILinkApprovalResult): void {
    this.applyRequestDetails(result?.requestType, result?.entityType, result?.entityName, result?.individualName, result?.requestedUserEmail);
    this.applyFinalDecisionDetails(result?.finalDecision, result?.finalDecisionByEmail, result?.finalDecisionAt);

    if (result?.approved) {
      this.state = 'approved';
      this.detailMessage = result.message || 'The account-link request was approved.';
      return;
    }

    if (result?.denied) {
      this.state = 'denied';
      this.detailMessage = result.message || 'The account-link request was denied.';
      return;
    }

    if (result?.alreadyApproved) {
      this.state = 'alreadyApproved';
      this.detailMessage = result.message || this.buildUnavailableMessage('This request was already approved.');
      return;
    }

    if (result?.alreadyDenied) {
      this.state = 'alreadyDenied';
      this.detailMessage = result.message || this.buildUnavailableMessage('This request was already denied.');
      return;
    }

    if (result?.expiredToken) {
      this.state = 'expired';
      this.detailMessage = result.message || this.buildUnavailableMessage('This approval link has expired.');
      return;
    }

    if (result?.usedToken) {
      this.state = 'used';
      this.detailMessage = result.message || this.buildUnavailableMessage('This approval link has already been used.');
      return;
    }

    if (result?.revokedToken) {
      this.state = 'revoked';
      this.detailMessage = result.message || this.buildUnavailableMessage('This approval link has been revoked.');
      return;
    }

    if (result?.invalidToken) {
      this.state = 'invalid';
      this.detailMessage = result.message || 'This approval link is invalid.';
      return;
    }

    this.state = 'error';
    this.detailMessage = result?.message || 'Unable to process this approval request right now.';
  }

  private applyRequestDetails(requestType: string, entityType: string, entityName: string, individualName: string, requestedUserEmail: string): void {
    this.requestType = requestType || this.requestType;
    this.entityType = this.resolveFriendlyRequestLabel(entityType, requestType) || this.entityType;
    this.entityName = entityName || this.entityName;
    this.individualName = individualName || this.individualName;
    this.requestedUserEmail = requestedUserEmail || this.requestedUserEmail;
  }

  private resolveFriendlyRequestLabel(entityType: string | null | undefined, requestType: string | null | undefined): string {
    const normalizedEntityType = (entityType || '').trim();
    if (normalizedEntityType.length) {
      return normalizedEntityType;
    }

    const normalizedRequestType = (requestType || '').trim();
    if (normalizedRequestType.toLowerCase().startsWith('individualaffiliationatp:')) {
      return 'Individual Affiliation to ATP';
    }

    if (normalizedRequestType.toLowerCase().startsWith('individualaffiliationfirm:')) {
      return 'Individual Affiliation to Firm';
    }

    if (normalizedRequestType.toLowerCase().startsWith('entitystaffaffiliationatp:')) {
      return 'ATP Staff Affiliation';
    }

    if (normalizedRequestType.toLowerCase().startsWith('entitystaffaffiliationfirm:')) {
      return 'Firm Staff Affiliation';
    }

    return normalizedRequestType;
  }

  private applyFinalDecisionDetails(finalDecision: string | null | undefined, finalDecisionByEmail: string | null | undefined, finalDecisionAt: string | null | undefined): void {
    const normalizedDecision = (finalDecision || '').trim();
    if (normalizedDecision.toLowerCase() === 'approved') {
      this.finalDecision = 'Approved';
    } else if (normalizedDecision.toLowerCase() === 'denied') {
      this.finalDecision = 'Denied';
    }

    this.finalDecisionByEmail = (finalDecisionByEmail || '').trim();
    this.finalDecisionAt = (finalDecisionAt || '').trim();
  }

  private buildUnavailableMessage(baseMessage: string): string {
    if (!this.finalDecision) {
      return baseMessage;
    }

    return `${baseMessage} ${this.finalDecisionSummary}`.trim();
  }

  private hasText(value: string | null | undefined): boolean {
    return (value || '').trim().length > 0;
  }

  private normalizeForComparison(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }
}
